import { Location } from '@angular/common';
import { Injectable, NgZone } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  Params,
  PRIMARY_OUTLET,
  Router,
  UrlSegment,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { List } from 'immutable';
import { isArray, isString, last, takeRight } from 'lodash';
import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { CookieService, WindowRefService } from 'i2g-ng-utils';
import {
  filter,
  map,
  pairwise,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { PARAMS } from '../billing-data/billing-data.data';
import { DialogService } from '../dialog/dialog.service';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Canvas } from '../features-data/sub-models/canvas.model';
import { IRouter } from '../shared/interfaces/router.interface';
import { LoggerService } from '../shared/services/logger/logger.service';
import { TrackingScreenNamesEnum } from '../tracking/tracking.data';
import { IDeepLinkProperties } from './deep-link-properties.interface';
import { DeepLinkTriggerActionsEnum } from './deep-link.enum';
import { NavigationBuilder } from './navigation.builder';
import { UrlService } from '../shared/services/url/url.service';
import { I2gDialogService } from 'i2g-ng-ui';
import { PmrDialogComponent } from '../shared/components/pmr-dialog/pmr-dialog.component';

@Injectable()
export class NavigationService {
  private _nativeWindow: any;
  private _previousUrls: string[] = [];
  private _lastUrlFromBackAction: string;
  private _navigationEnds: Subject<string> = new Subject<string>();

  navigationEnds$: Observable<string> = this._navigationEnds
    .asObservable()
    .pipe(
      tap((url: string) => this._loggerService.log('navigating to:', url)),
      shareReplay(1)
    );

  routeData$: BehaviorSubject<any> = new BehaviorSubject<any>({});

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _urlService: UrlService,
    private _navigationBuilder: NavigationBuilder,
    private _loggerService: LoggerService,
    private _windowRefService: WindowRefService,
    private _ngZone: NgZone,
    private _dialogService: DialogService,
    private _i2gDialogService: I2gDialogService,
    private _cookieService: CookieService,
    private _featuresDataStore: FeaturesDataStore,
    private _store: Store<any>,
    private _location: Location
  ) {
    this._nativeWindow = this._windowRefService.nativeWindow;

    _router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: any) => event.urlAfterRedirects),
        tap((url) => this._navigationEnds.next(url)),
        pairwise()
      )
      .subscribe(([previousUrl, _]: string[]) => {
        // - only update previousUrls on NavigationEnd so guards don't affect the stack;
        this._pushPreviousUrls(previousUrl);

        this._lastUrlFromBackAction = null;
      });

    this.navigationEnds$.subscribe(() => {
      this._nativeWindow.scrollTo(0, 0);
      this.routeData$.next(this._getDataFromActiveRoute());
    });
  }

  // adapted from:
  // https://stackoverflow.com/questions/40863664/how-to-get-current-route-custom-data-in-angular-2
  private _getDataFromActiveRoute() {
    let child = this._route;
    let data = { ...child.snapshot.data };
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
        data = { ...data, ...child.snapshot.data };
      } else if (child.snapshot.data) {
        return data;
      } else {
        return null;
      }
    }
    return null;
  }

  get activeRouteName(): TrackingScreenNamesEnum {
    return this._routeNameFromData(this.routeData$.value);
  }

  get activeRouteName$(): Observable<TrackingScreenNamesEnum> {
    return this.routeData$.pipe(
      map((data) => this._routeNameFromData(data)),
      shareReplay(1)
    );
  }

  private _routeNameFromData(data: any): TrackingScreenNamesEnum {
    const name = data.name;
    const prefix = data.prefixName;
    const prefix_name = [];
    if (prefix) {
      prefix_name.push(prefix);
    }
    if (name) {
      prefix_name.push(name);
    }
    return prefix_name.join('_') as TrackingScreenNamesEnum;
  }

  refresh() {
    this._windowRefService.nativeWindow.location.reload();
  }

  goTo(
    route: string | string[],
    navigationExtras?: NavigationExtras
  ): Promise<boolean> {
    // fix for APPS-763
    // https://stackoverflow.com/questions/48341085/angular-change-detection-not-triggering-on-router-navigate
    return this._ngZone.run(() =>
      this._router.navigate(isArray(route) ? route : [route], navigationExtras)
    );
  }

  goToByUrl(
    url: string,
    navigationExtras?: NavigationExtras
  ): Promise<boolean> {
    // fix for APPS-763
    // https://stackoverflow.com/questions/48341085/angular-change-detection-not-triggering-on-router-navigate
    return this._ngZone.run(() =>
      this._router.navigateByUrl(url, navigationExtras)
    );
  }

  goBack(
    goBackUrl?: string | string[],
    navigationExtras?: NavigationExtras
  ): Promise<boolean> {
    if (this._nativeWindow.document.referrer) {
      this._location.back();
      return;
    }

    const lastUrl = goBackUrl || this.getLastUrl();

    this._lastUrlFromBackAction = this.getCurrentUrl();

    if (lastUrl && isString(lastUrl)) {
      const goBackRoute = this._urlService.parseUrlToRoute(
        lastUrl,
        navigationExtras
      );

      return new Promise((resolve) => {
        this.goTo(goBackRoute.route, goBackRoute.navigationExtras); // deactivation guard breaks the Location.back()
        // history see
        // https://github.com/angular/angular/issues/13586
        resolve();
      });
    } else {
      return this.goTo(lastUrl || '/', navigationExtras);
    }
  }

  goToExternalUrl(url: string | string[], newTab: boolean = false) {
    url = isArray(url) ? Location.stripTrailingSlash(url.join('/')) : url;

    if (newTab) {
      this._nativeWindow.open(url, '_blank');
    } else {
      (this._nativeWindow.top || this._nativeWindow).location.href = url;
    }
  }

  getLocationQueryParams(asString?: boolean): Params | string {
    const queryParams: Params = this._urlService.parse(this.getLocationHref())
      .query;

    return asString
      ? this._urlService.serializeParams(queryParams)
      : queryParams;
  }

  isRouteActive(
    route: string[],
    navigationExtras?: NavigationExtras,
    exact?: boolean
  ): boolean {
    return this._router.isActive(
      this._router.createUrlTree(route, navigationExtras || {}),
      exact
    );
  }

  getCurrentUrl(): string {
    return this._router.url;
  }

  getLocationPathname(): string {
    return this._nativeWindow.location.pathname;
  }

  getLocationHref(): string {
    return this._nativeWindow.location.href;
  }

  getLocationOrigin(): string {
    return this._nativeWindow.location.origin;
  }

  getLastUrl(): string {
    return last(this._previousUrls);
  }

  getLastUrlFromBackAction(): string {
    return this._lastUrlFromBackAction;
  }

  getPreviousUrls(): string[] {
    return this._previousUrls;
  }

  goToDeepLink(deepLink: string) {
    const deepLinkProperties: IDeepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
      deepLink
    );

    switch (deepLinkProperties.triggerAction) {
      case DeepLinkTriggerActionsEnum.paywall:
      // fall through
      case DeepLinkTriggerActionsEnum.purchase:
      // fall through
      case DeepLinkTriggerActionsEnum.navigate:
        const routeInfo: IRouter = this._navigationBuilder.buildRouteFromDeepLinkProperties(
          deepLinkProperties
        );
        const action: any = this._navigationBuilder.buildActionFromDeeplinkProperties(
          deepLinkProperties
        );

        if (action) {
          this._store.dispatch(action);
        }

        if (routeInfo.route) {
          this.goTo(routeInfo.route, routeInfo.navigationExtras);
        }

        break;
      case DeepLinkTriggerActionsEnum.opt_out_trial:
        if (this._cookieService.getCookie(PARAMS.PROMO_CODE)) {
          this._featuresDataStore.canvases$
            .pipe(
              switchMap((canvases: List<Canvas>) => from(canvases.toArray())),
              filter(
                (canvas: Canvas) =>
                  canvas.name === deepLinkProperties.params.canvas
              ),
              take(1)
            )
            .subscribe((canvas: Canvas) => {
              this.goToDeepLink(canvas.canvasConfig.cta1Uri);
            });
          break;
        }

        this._i2gDialogService
          .openDialog(PmrDialogComponent, {
            deeplinkParams: this._navigationBuilder.buildDeepLinkProperties(
              deepLink
            ).params,
          })
          .subscribe((payload) => this.goToDeepLink(payload));

        break;
      case DeepLinkTriggerActionsEnum.canvas:
        const {
          canvasType,
          featureName,
        } = this._navigationBuilder.buildGrowthDialogDataFromDeepLinkProperties(
          deepLinkProperties
        );

        // for now, the blocked account canvas is hardcoded on the client side, so don't display it
        if (featureName === TrackingScreenNamesEnum.blocked_by_email) {
          return;
        }

        this._dialogService.openGrowthDialog(canvasType, featureName);

        break;
      case DeepLinkTriggerActionsEnum.external_uri:
        this.goToExternalUrl(deepLinkProperties.path, true);
        break;
      case DeepLinkTriggerActionsEnum.action:
        // this is hacky and a very poor implementation :(
        // leave it here until we figure out a better solution
        const redirectInfo: IRouter = this._navigationBuilder.buildRouteFromDeepLinkProperties(
          {
            ...deepLinkProperties,
            triggerAction: DeepLinkTriggerActionsEnum.navigate,
          }
        );

        if (redirectInfo) {
          this.goTo(redirectInfo.route, redirectInfo.navigationExtras);
        }

        const storeAction: any = this._navigationBuilder.buildActionFromDeeplinkProperties(
          deepLinkProperties
        );

        if (storeAction) {
          this._store.dispatch(storeAction);
        }

        break;
    }
  }

  private _pushPreviousUrls(url: string) {
    this._previousUrls.push(url);

    if (this._previousUrls.length > 100) {
      this._previousUrls = takeRight(this._previousUrls, 50);
    }
  }

  getPathSegments(_path: string = ''): UrlSegment[] {
    const path = _path || this.getLocationPathname();
    return this._router.parseUrl(path).root.children[PRIMARY_OUTLET].segments;
  }
}
